import type { EChartsOption, TooltipComponentFormatterCallbackParams } from 'echarts';
import ReactECharts, { type EChartsReactProps } from 'echarts-for-react';
import { type FC, memo, useMemo } from 'react';

export interface Marker {
  name: string;
  coordinateIndex: number;
  color?: string;
  highlight?: boolean;
  offset?: number;
}
export type TooltipFormatter = (
  params: TooltipComponentFormatterCallbackParams,
  ticket: string,
  callback: (ticket: string, html: string) => string,
) => string | HTMLElement | Array<HTMLElement>;

interface Props extends EChartsReactProps {
  onHoverPosition?: (index: number | null) => void;
  onClickPosition?: (index: number) => void;
  onHoverMarker?: (marker: Marker | null) => void;
  option: EChartsOption;
}

/**
 * A wrapper component for ReactECharts that adds hover and click event handling capabilities.
 * It intercepts chart events to provide simplified interaction callbacks and maintains the original event handling functionality.
 *
 * @component
 * @param {Object} props - Component props
 * @param {function} [props.onHoverPosition] - Callback fired when hovering over a point in the line series. Receives the data index or null when hover ends.
 * @param {function} [props.onClickPosition] - Callback fired when clicking on a point in the line series. Receives the data index.
 * @param {function} [props.onHoverMarker] - Callback fired when hovering over a marker. Receives the marker object or null when hover ends.
 * @param {Object} props.option - ECharts option configuration object.
 * @param {Object} [props.onEvents] - Original ECharts event callbacks that will still be called alongside the enhanced handlers.
 * @param {...any} props.rest - Additional props passed to the underlying ReactECharts component.
 *
 * @returns {ReactNode} ReactECharts component with enhanced event handling
 *
 * @example
 * <ChartWithHover
 *   option={chartOptions}
 *   onHoverPosition={(index) => console.log('Hovering over point', index)}
 *   onClickPosition={(index) => console.log('Clicked on point', index)}
 * />
 */
export const ChartWithHoverAndClickCallback: FC<Props> = memo(function ChartWithHover({
  onHoverPosition,
  onClickPosition,
  onHoverMarker,
  option,
  onEvents,
  ...props
}: Props) {
  const eventHandlers = {
    click: (params: TooltipComponentFormatterCallbackParams) => {
      onEvents?.click?.(params);
      if (Array.isArray(params)) {
        eventHandlers.mouseover(params[0]);
        return;
      }

      const index = params.dataIndex;
      if (params.componentType === 'series' && params.componentSubType === 'line') {
        onClickPosition?.(index);
      }
    },
    mouseover: (params: TooltipComponentFormatterCallbackParams) => {
      onEvents?.mouseover?.(params);
      if (Array.isArray(params)) {
        eventHandlers.mouseover(params[0]);
        return;
      }

      const index = params.dataIndex;
      if (params.componentType === 'series' && params.componentSubType === 'line') {
        onHoverPosition?.(index);
        return;
      }

      if (params.componentType === 'markLine') {
        // @ts-expect-error data field is setted with object
        const marker = markers.find((m) => m.coordinateIndex === params.data.pointIndex);

        if (marker) onHoverMarker?.(marker);
        return;
      }
    },
    mouseout: (params: TooltipComponentFormatterCallbackParams) => {
      onEvents?.mouseout?.(params);
      if (Array.isArray(params)) {
        eventHandlers.mouseout(params[0]);
        return;
      }

      if (params.componentType === 'markLine') {
        onHoverMarker?.(null);
      }
    },
    globalout: () => {
      onEvents?.globalout?.();
      onHoverPosition?.(null);
    },
  };
  const optionWithHoverCallback = useMemo(
    () => ({
      ...option,
      tooltip: {
        ...option.tooltip,
        formatter: (
          params: TooltipComponentFormatterCallbackParams,
          ticket: string,
          callback: (ticket: string, html: string) => string,
        ) => {
          const customFormatter = Array.isArray(option.tooltip)
            ? option.tooltip[0].formatter
            : option.tooltip?.formatter;

          eventHandlers.mouseover?.(params);
          return customFormatter || defaultSeriesFormatTooltip(params, ticket, callback);
        },
      },
    }),
    [option, onHoverPosition],
  );

  return <ReactECharts {...props} option={optionWithHoverCallback} onEvents={eventHandlers} />;
});

const defaultSeriesFormatTooltip: TooltipFormatter = (params, ticket, callback) => {
  if (Array.isArray(params)) {
    return params.map((p) => defaultSeriesFormatTooltip(p, ticket, callback)).join('');
  }
  if (params.componentType === 'series') {
    const { seriesName, value } = params;
    const formattedValue = Array.isArray(value) ? value.join(', ') : value;
    return callback(ticket, `<strong>${seriesName}</strong>: ${formattedValue}`);
  }
  return callback(ticket, `<strong>${params.seriesName}</strong>: ${params.value}`);
};
